import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, RefreshCw } from 'lucide-react';
import {
  useICalFeeds,
  useConfigureICalFeed,
  useRemoveICalFeed,
  useSyncICalFeedNow,
} from '../../hooks/useICalFeed';
import { IcalActionErrorCode } from '../../services/icalFeedService';
import { ConfirmDialog } from '../ConfirmDialog';
import { ICalFeedConfiguration, ICalFeedSourceKey } from '../../types/icalFeed';
import { ICalFeedPanel } from './ICalFeedPanel';

interface ICalFeedSectionProps {
  readonly apartmentId: string;
}

const isValidFeedUrl = (url: string): boolean =>
  url.trim().length > 0 && url.trim().startsWith('https://');

const SOURCE_OPTIONS: readonly ICalFeedSourceKey[] = [
  'AIRBNB',
  'BOOKING',
  'EXPEDIA',
  'FEWO_DIREKT',
  'VRBO',
  'TRIPADVISOR',
  'CUSTOM',
];

const getFeedLocalKey = (feed: ICalFeedConfiguration, index: number): string =>
  feed.icalFeedConfigurationId ?? `${feed.sourceKey ?? 'UNKNOWN'}-${index}`;

const isKnownSourceKey = (sourceKey: ICalFeedSourceKey): boolean =>
  sourceKey !== 'CUSTOM';

interface ICalFeedSectionLabels {
  readonly sourceAirbnb: string;
  readonly sourceBooking: string;
  readonly sourceExpedia: string;
  readonly sourceFewoDirekt: string;
  readonly sourceVrbo: string;
  readonly sourceTripadvisor: string;
  readonly sourceOther: string;
  readonly removeContractTerminationConfirm: (source: string) => string;
}

const getSourceLabel = (feed: ICalFeedConfiguration, labels: ICalFeedSectionLabels): string => {
  if (feed.sourceKey === 'AIRBNB') return labels.sourceAirbnb;
  if (feed.sourceKey === 'BOOKING') return labels.sourceBooking;
  if (feed.sourceKey === 'EXPEDIA') return labels.sourceExpedia;
  if (feed.sourceKey === 'FEWO_DIREKT') return labels.sourceFewoDirekt;
  if (feed.sourceKey === 'VRBO') return labels.sourceVrbo;
  if (feed.sourceKey === 'TRIPADVISOR') return labels.sourceTripadvisor;
  if (feed.sourceLabel) return feed.sourceLabel;
  return labels.sourceOther;
};

const getDeleteConfirmationMessage = (
  feed: ICalFeedConfiguration,
  labels: ICalFeedSectionLabels
): string => {
  return labels.removeContractTerminationConfirm(getSourceLabel(feed, labels));
};

/**
 * ICalFeedSection — renders the Airbnb iCal feed configuration for a single apartment.
 *
 * Covers:
 *  US1 — Configure / update the feed URL
 *  US2 — View current feed status (last synced, sync error)
 *  US3 — Remove the feed URL (with confirm dialog)
 *
 * US4 (ICalEventsTable) is rendered below this section from index.tsx after T019.
 */
export const ICalFeedSection = ({ apartmentId }: ICalFeedSectionProps) => {
  const { t } = useTranslation(['pages', 'errors', 'forms']);

  const labels: ICalFeedSectionLabels = {
    sourceAirbnb: t('pages:icalFeedSourceAirbnb'),
    sourceBooking: t('pages:icalFeedSourceBooking'),
    sourceExpedia: t('pages:icalFeedSourceExpedia'),
    sourceFewoDirekt: t('pages:icalFeedSourceFewoDirekt', 'FeWo-direkt'),
    sourceVrbo: t('pages:icalFeedSourceVrbo', 'Vrbo'),
    sourceTripadvisor: t('pages:icalFeedSourceTripadvisor', 'TripAdvisor'),
    sourceOther: t('pages:icalFeedSourceOther'),
    removeContractTerminationConfirm: source =>
      t('pages:removeICalFeedContractTerminationConfirm', { source }),
  };

  const { data: feeds = [], isLoading, isError, refetch } = useICalFeeds(apartmentId);
  const configureMutation = useConfigureICalFeed();
  const removeMutation = useRemoveICalFeed();
  const syncNowMutation = useSyncICalFeedNow();

  const [newFeedSourceKey, setNewFeedSourceKey] = useState<ICalFeedSourceKey>('AIRBNB');
  const [customSourceLabel, setCustomSourceLabel] = useState<string>('');
  const [newFeedUrl, setNewFeedUrl] = useState<string>('');
  const [createUrlError, setCreateUrlError] = useState<string>('');
  const [createSourceLabelError, setCreateSourceLabelError] = useState<string>('');
  const [createError, setCreateError] = useState<string>('');
  const [rowInputByKey, setRowInputByKey] = useState<Record<string, string>>({});
  const [rowSaveErrorByKey, setRowSaveErrorByKey] = useState<Record<string, string>>({});
  const [rowRemoveErrorByKey, setRowRemoveErrorByKey] = useState<Record<string, string>>({});
  const [rowSyncErrorByKey, setRowSyncErrorByKey] = useState<Record<string, string>>({});

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    message: string;
    onConfirm: () => void;
  }>({ open: false, message: '', onConfirm: () => void 0 });

  const configuredSources = new Set(
    feeds.map(feed => feed.sourceKey).filter((key): key is ICalFeedSourceKey => !!key)
  );
  const availableSourceOptions = SOURCE_OPTIONS.filter(sourceKey => !configuredSources.has(sourceKey));

  const handleUpdateFeed = async (feed: ICalFeedConfiguration, index: number) => {
    const feedKey = getFeedLocalKey(feed, index);
    const candidateUrl = (rowInputByKey[feedKey] ?? feed.feedUrl ?? '').trim();

    setCreateError('');
    setRowSaveErrorByKey(current => ({ ...current, [feedKey]: '' }));
    setCreateUrlError('');

    if (!isValidFeedUrl(candidateUrl)) {
      setRowSaveErrorByKey(current => ({
        ...current,
        [feedKey]: t('errors:invalidICalFeedUrl'),
      }));
      return;
    }

    try {
      await configureMutation.mutateAsync({
        apartmentId,
        icalFeedConfigurationId: feed.icalFeedConfigurationId,
        sourceKey: feed.sourceKey,
        request: { feedUrl: candidateUrl },
      });

      setRowInputByKey(current => {
        const next = { ...current };
        delete next[feedKey];
        return next;
      });
    } catch {
      setRowSaveErrorByKey(current => ({
        ...current,
        [feedKey]: t('errors:failedToSaveICalFeed'),
      }));
    }
  };

  const handleCreateFeed = async () => {
    const url = newFeedUrl.trim();
    setCreateUrlError('');
    setCreateSourceLabelError('');
    setCreateError('');

    if (!isValidFeedUrl(url)) {
      setCreateUrlError(t('errors:invalidICalFeedUrl'));
      return;
    }

    const trimmedCustomLabel = customSourceLabel.trim();
    if (newFeedSourceKey === 'CUSTOM') {
      if (trimmedCustomLabel.length === 0) {
        setCreateSourceLabelError(t('errors:invalidICalFeedSourceLabel'));
        return;
      }
      if (trimmedCustomLabel.length > 100) {
        setCreateSourceLabelError(t('errors:invalidICalFeedSourceLabel'));
        return;
      }
    }

    if (isKnownSourceKey(newFeedSourceKey) && configuredSources.has(newFeedSourceKey)) {
      setCreateError(t('pages:icalFeedSourceAlreadyConfigured', 'This source is already configured.'));
      return;
    }

    try {
      await configureMutation.mutateAsync({
        apartmentId,
        sourceKey: isKnownSourceKey(newFeedSourceKey) ? newFeedSourceKey : undefined,
        sourceLabel: newFeedSourceKey === 'CUSTOM' ? trimmedCustomLabel : undefined,
        request: { feedUrl: url },
      });
      setNewFeedUrl('');
      setCustomSourceLabel('');
      const nextDefault = SOURCE_OPTIONS.find(sourceKey => sourceKey !== newFeedSourceKey);
      if (nextDefault) {
        setNewFeedSourceKey(nextDefault);
      }
    } catch {
      setCreateError(t('errors:failedToSaveICalFeed'));
    }
  };

  // ── loading state ────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="mt-4 p-4 border rounded-lg bg-gray-50">
        <div className="h-4 bg-gray-200 rounded animate-pulse w-1/3 mb-2" />
        <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2" />
      </div>
    );
  }

  // ── query error state (non-404) ──────────────────────────────────────────
  if (isError) {
    return (
      <div className="mt-4 p-4 border border-red-200 rounded-lg bg-red-50">
        <div className="flex items-center gap-2 text-red-700 mb-2">
          <AlertCircle size={16} />
          <span className="text-sm font-medium">{t('errors:failedToLoadICalFeed')}</span>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-1 text-xs text-red-600 hover:text-red-800 underline"
        >
          <RefreshCw size={12} />
          {t('common:retry', 'Retry')}
        </button>
      </div>
    );
  }

  // ── handlers ─────────────────────────────────────────────────────────────
  const handleRemoveClick = (feed: ICalFeedConfiguration, index: number) => {
    const feedKey = getFeedLocalKey(feed, index);
    setRowRemoveErrorByKey(current => ({ ...current, [feedKey]: '' }));
    setConfirmDialog({
      open: true,
      message: getDeleteConfirmationMessage(feed, labels),
      onConfirm: async () => {
        setConfirmDialog(d => ({ ...d, open: false }));
        try {
          await removeMutation.mutateAsync({
            apartmentId,
            icalFeedConfigurationId: feed.icalFeedConfigurationId,
          });
        } catch {
          setRowRemoveErrorByKey(current => ({
            ...current,
            [feedKey]: t('errors:failedToRemoveICalFeed'),
          }));
        }
      },
    });
  };

  const handleSyncNow = async (feed: ICalFeedConfiguration, index: number) => {
    const feedKey = getFeedLocalKey(feed, index);
    setRowSyncErrorByKey(current => ({ ...current, [feedKey]: '' }));

    if (!feed.icalFeedConfigurationId) {
      return;
    }

    try {
      await syncNowMutation.mutateAsync({
        apartmentId,
        icalFeedConfigurationId: feed.icalFeedConfigurationId,
      });
    } catch (err) {
      const code = (err as { code?: IcalActionErrorCode })?.code ?? 'UNKNOWN';
      setRowSyncErrorByKey(current => ({
        ...current,
        [feedKey]:
          code === 'FEED_NOT_ACTIVE'
            ? t('errors:icalFeedNotActiveForSync')
            : t('errors:failedToSyncICalFeed'),
      }));
    }
  };

  const hasConfiguredFeeds = feeds.length > 0;

  return (
    <div className="mt-4 p-4 border rounded-lg bg-white">
      {/* Heading */}
      <h4 className="text-sm font-semibold text-gray-800 mb-1">
        {t('pages:icalFeedsHeading', 'iCal Feeds')}
      </h4>
      <p className="text-xs text-gray-500 mb-3">{t('pages:icalFeedSubHeading')}</p>

      {!hasConfiguredFeeds && (
        <p className="mb-3 text-xs text-gray-500">{t('pages:icalFeedNotConfigured')}</p>
      )}

      {feeds.map((feed, index) => {
        const feedKey = getFeedLocalKey(feed, index);
        const displayUrl = rowInputByKey[feedKey] ?? feed.feedUrl ?? '';
        const rowSaveError = rowSaveErrorByKey[feedKey];
        const rowRemoveError = rowRemoveErrorByKey[feedKey];
        const rowSyncError = rowSyncErrorByKey[feedKey];

        return (
          <ICalFeedPanel
            key={feedKey}
            apartmentId={apartmentId}
            feed={feed}
            feedKey={feedKey}
            sourceLabel={getSourceLabel(feed, labels)}
            displayUrl={displayUrl}
            rowSaveError={rowSaveError}
            rowRemoveError={rowRemoveError}
            rowSyncError={rowSyncError}
            isSaving={configureMutation.isPending}
            isRemoving={removeMutation.isPending}
            isSyncing={syncNowMutation.isPending}
            onChangeUrl={value => {
              setRowInputByKey(current => ({ ...current, [feedKey]: value }));
              setRowSaveErrorByKey(current => ({ ...current, [feedKey]: '' }));
            }}
            onSave={() => void handleUpdateFeed(feed, index)}
            onRemove={() => handleRemoveClick(feed, index)}
            onSyncNow={() => void handleSyncNow(feed, index)}
          />
        );
      })}

      <div className="rounded-lg border border-dashed border-gray-300 p-3">
        <h5 className="text-xs font-semibold text-gray-700 mb-2">
          {t('pages:addICalFeedHeading', 'Add iCal feed')}
        </h5>

        <div className="mb-2 grid grid-cols-1 gap-2 md:grid-cols-[180px_1fr]">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor={`ical-feed-source-${apartmentId}`}>
              {t('pages:icalFeedSourceLabel', 'Source')}
            </label>
            <select
              id={`ical-feed-source-${apartmentId}`}
              value={newFeedSourceKey}
              onChange={e => {
                setNewFeedSourceKey(e.target.value as ICalFeedSourceKey);
                setCreateSourceLabelError('');
                setCreateError('');
              }}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {SOURCE_OPTIONS.map(sourceKey => (
                <option
                  key={sourceKey}
                  value={sourceKey}
                  disabled={isKnownSourceKey(sourceKey) ? configuredSources.has(sourceKey) : false}
                >
                  {sourceKey === 'AIRBNB'
                    ? labels.sourceAirbnb
                    : sourceKey === 'BOOKING'
                      ? labels.sourceBooking
                      : sourceKey === 'EXPEDIA'
                        ? labels.sourceExpedia
                        : sourceKey === 'FEWO_DIREKT'
                          ? labels.sourceFewoDirekt
                        : sourceKey === 'VRBO'
                            ? labels.sourceVrbo
                            : sourceKey === 'TRIPADVISOR'
                              ? labels.sourceTripadvisor
                              : t('pages:icalFeedSourceCustom', 'Custom')}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor={`ical-feed-new-url-${apartmentId}`}
              className="block text-xs font-medium text-gray-700 mb-1"
            >
              {t('forms:icalFeedUrl')}
            </label>
            <input
              id={`ical-feed-new-url-${apartmentId}`}
              type="url"
              value={newFeedUrl}
              onChange={e => {
                setNewFeedUrl(e.target.value);
                setCreateUrlError('');
              }}
              placeholder={t('forms:icalFeedUrlPlaceholder')}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {newFeedSourceKey === 'CUSTOM' && (
          <div className="mb-2">
            <label
              htmlFor={`ical-feed-custom-source-label-${apartmentId}`}
              className="block text-xs font-medium text-gray-700 mb-1"
            >
              {t('pages:icalFeedCustomSourceLabel', 'Custom source label')}
            </label>
            <input
              id={`ical-feed-custom-source-label-${apartmentId}`}
              type="text"
              maxLength={100}
              value={customSourceLabel}
              onChange={e => {
                setCustomSourceLabel(e.target.value);
                setCreateSourceLabelError('');
              }}
              placeholder={t('pages:icalFeedCustomSourceLabelPlaceholder', 'e.g. FeWo-direkt')}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        )}

        {createUrlError && (
          <p className="mb-2 text-xs text-red-600" role="alert">{createUrlError}</p>
        )}
        {createSourceLabelError && (
          <p className="mb-2 text-xs text-red-600" role="alert">{createSourceLabelError}</p>
        )}
        {createError && (
          <p className="mb-2 text-xs text-red-600" role="alert">{createError}</p>
        )}

        <button
          onClick={() => void handleCreateFeed()}
          disabled={configureMutation.isPending || availableSourceOptions.length === 0}
          className="px-3 py-1.5 text-xs font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {configureMutation.isPending
            ? t('pages:savingFeedUrl')
            : t('pages:addFeed', 'Add Feed')}
        </button>
      </div>

      {/* Confirm dialog (US3) */}
      <ConfirmDialog
        open={confirmDialog.open}
        message={confirmDialog.message}
        intent="danger"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(d => ({ ...d, open: false }))}
      />

    </div>
  );
};

export default ICalFeedSection;
